/**
 * Feedback markup language support for CodeMirror 6
 *
 * Provides syntax highlighting for feedback comments that can reference
 * lyrics text and recordings with timestamps.
 *
 * Syntax:
 * - {@"lyrics text"} - Reference specific lyrics (highlighted in song view)
 * - [Recording Name @ 1:30] - Link to recording at specific timestamp
 */
import { LanguageSupport, LRLanguage } from "@codemirror/language";
import { styleTags, tags as t } from "@lezer/highlight";
import { parser } from "./parser.js";

/**
 * Parser configured with syntax highlighting metadata
 */
const feedbackParser = parser.configure({
  props: [
    styleTags({
      // Lyrics anchors - use special tag for emphasis
      LyricsAnchor: t.special(t.string),
      AnchorText: t.string,
      anchorStart: t.brace,
      anchorEnd: t.brace,

      // Recording links - like hyperlinks
      RecordingLink: t.link,
      RecordingName: t.labelName,
      Timestamp: t.number,
      TimestampSection: t.meta,
      linkStart: t.squareBracket,
      linkEnd: t.squareBracket,
      atSign: t.operator,

      // Plain text - regular content
      PlainText: t.content,
    }),
  ],
});

/**
 * Feedback markup language definition for CodeMirror
 */
export const feedbackLanguage = LRLanguage.define({
  name: "feedback",
  parser: feedbackParser,
  languageData: {
    closeBrackets: {
      brackets: ["[", "{"],
    },
  },
});

/**
 * Feedback markup language support for CodeMirror 6
 *
 * Usage:
 * ```tsx
 * import { feedback } from "@bandwith/lezer-feedback";
 * import CodeMirror from "@uiw/react-codemirror";
 *
 * <CodeMirror extensions={[feedback()]} />
 * ```
 */
export function feedback(): LanguageSupport {
  return new LanguageSupport(feedbackLanguage);
}

// Re-export for advanced usage (parsing without CodeMirror)
export { feedbackParser, parser };

export type ParsedFeedback = {
  /** Lyrics text snippets to highlight */
  anchors: string[];
  /** Recording references with optional timestamps */
  recordingLinks: Array<{
    name: string;
    timestamp: string | null;
  }>;
  /** Plain text content with markup stripped */
  displayContent: string;
};

type Range = { from: number; to: number };

/**
 * Check if a node or any of its children contain errors.
 */
function nodeHasError(
  content: string,
  nodeFrom: number,
  nodeTo: number,
): boolean {
  const tree = parser.parse(content);
  let hasError = false;

  tree.iterate({
    from: nodeFrom,
    to: nodeTo,
    enter(node) {
      if (node.type.isError || node.name === "⚠") {
        hasError = true;
        return false;
      }
    },
  });

  return hasError;
}

/**
 * Collect ranges of LyricsAnchor nodes that have no parse errors.
 */
function findValidAnchorRanges(
  content: string,
  tree: ReturnType<typeof parser.parse>,
): Range[] {
  const ranges: Range[] = [];
  tree.iterate({
    enter(node) {
      if (node.name === "LyricsAnchor") {
        if (!nodeHasError(content, node.from, node.to)) {
          ranges.push({ from: node.from, to: node.to });
        }
      }
    },
  });
  return ranges;
}

/**
 * Unescape anchor text (reverse the escaping from createAnchorMarkup).
 */
function unescapeAnchorText(text: string): string {
  return text.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
}

/**
 * Parse feedback content and extract anchors, links, and display text.
 *
 * @example
 * parseFeedback('{@"always love you"} Try harmony here [Demo @ 1:30]')
 * // => {
 * //   anchors: ["always love you"],
 * //   recordingLinks: [{ name: "Demo", timestamp: "1:30" }],
 * //   displayContent: "Try harmony here"
 * // }
 */
export function parseFeedback(content: string): ParsedFeedback {
  const tree = parser.parse(content);
  const anchors: string[] = [];
  const recordingLinks: ParsedFeedback["recordingLinks"] = [];
  const displayParts: string[] = [];

  const validAnchorRanges = findValidAnchorRanges(content, tree);

  // Extract data
  tree.iterate({
    enter(node) {
      if (node.name === "AnchorText") {
        const isValid = validAnchorRanges.some(
          (range) => node.from >= range.from && node.to <= range.to,
        );
        if (isValid) {
          anchors.push(unescapeAnchorText(content.slice(node.from, node.to)));
        }
      } else if (node.name === "RecordingName") {
        const name = content.slice(node.from, node.to).trim();
        recordingLinks.push({ name, timestamp: null });
      } else if (node.name === "Timestamp") {
        const lastLink = recordingLinks[recordingLinks.length - 1];
        if (lastLink) {
          lastLink.timestamp = content.slice(node.from, node.to);
        }
      } else if (node.name === "PlainText") {
        displayParts.push(content.slice(node.from, node.to));
      } else if (node.name === "LyricsAnchor") {
        // Check if this is an invalid anchor - if so, include raw text in display
        const isValid = validAnchorRanges.some(
          (range) => range.from === node.from && range.to === node.to,
        );
        if (!isValid) {
          displayParts.push(content.slice(node.from, node.to));
        }
      } else if (node.type.isError || node.name === "⚠") {
        // Include error content in display (for malformed syntax)
        const errorText = content.slice(node.from, node.to);
        if (errorText) {
          displayParts.push(errorText);
        }
      }
    },
  });

  const displayContent = displayParts.join("").replace(/\s+/g, " ").trim();

  return { anchors, recordingLinks, displayContent };
}

/**
 * Check if feedback content contains any valid (complete) lyrics anchors.
 */
export function hasLyricsAnchors(content: string): boolean {
  const tree = parser.parse(content);
  return findValidAnchorRanges(content, tree).length > 0;
}

/**
 * Create anchor markup for a given text selection.
 * Used when users select lyrics and click "Add Comment at Selection".
 */
export function createAnchorMarkup(text: string): string {
  // Escape backslashes first, then quotes (order matters!)
  const escapedText = text.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `{@"${escapedText}"} `;
}

/**
 * A positioned element from parsed feedback markup.
 */
export type PositionedElement =
  | { type: "text"; content: string; from: number; to: number }
  | { type: "lyrics"; anchorText: string; from: number; to: number }
  | {
      type: "recording";
      name: string;
      timestamp: string | null;
      from: number;
      to: number;
    };

/**
 * Parse feedback content and return elements with their positions.
 * Useful for rendering feedback body with interleaved text and markup.
 *
 * @example
 * parseFeedbackWithPositions('{@"verse"} Try this [Demo @ 1:30]')
 * // => [
 * //   { type: "lyrics", anchorText: "verse", from: 0, to: 11 },
 * //   { type: "text", content: " Try this ", from: 11, to: 21 },
 * //   { type: "recording", name: "Demo", timestamp: "1:30", from: 21, to: 35 }
 * // ]
 */
export function parseFeedbackWithPositions(
  content: string,
): PositionedElement[] {
  const tree = parser.parse(content);
  const validAnchorRanges = findValidAnchorRanges(content, tree);

  // Collect non-text elements (anchors and recording links)
  type MarkupElement = Exclude<PositionedElement, { type: "text" }>;
  const markupElements: MarkupElement[] = [];

  // Collect lyrics anchors
  tree.iterate({
    enter(node) {
      if (node.name === "AnchorText") {
        const parentRange = validAnchorRanges.find(
          (r) => node.from >= r.from && node.to <= r.to,
        );
        if (parentRange) {
          markupElements.push({
            type: "lyrics",
            anchorText: unescapeAnchorText(content.slice(node.from, node.to)),
            from: parentRange.from,
            to: parentRange.to,
          });
        }
      }
    },
  });

  // Collect recording links
  let currentRecording: {
    from: number;
    to: number;
    name: string;
    timestamp: string | null;
  } | null = null;

  tree.iterate({
    enter(node) {
      if (node.name === "RecordingLink") {
        currentRecording = {
          from: node.from,
          to: node.to,
          name: "",
          timestamp: null,
        };
      } else if (node.name === "RecordingName" && currentRecording) {
        currentRecording.name = content.slice(node.from, node.to).trim();
      } else if (node.name === "Timestamp" && currentRecording) {
        currentRecording.timestamp = content.slice(node.from, node.to);
      }
    },
    leave(node) {
      if (node.name === "RecordingLink" && currentRecording) {
        if (currentRecording.name) {
          markupElements.push({
            type: "recording",
            name: currentRecording.name,
            timestamp: currentRecording.timestamp,
            from: currentRecording.from,
            to: currentRecording.to,
          });
        }
        currentRecording = null;
      }
    },
  });

  // Sort by position and interleave with text
  markupElements.sort((a, b) => a.from - b.from);

  const elements: PositionedElement[] = [];
  let lastEnd = 0;
  for (const el of markupElements) {
    if (el.from > lastEnd) {
      elements.push({
        type: "text",
        content: content.slice(lastEnd, el.from),
        from: lastEnd,
        to: el.from,
      });
    }
    elements.push(el);
    lastEnd = el.to;
  }

  if (lastEnd < content.length) {
    elements.push({
      type: "text",
      content: content.slice(lastEnd),
      from: lastEnd,
      to: content.length,
    });
  }

  return elements;
}
